import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import Box from '@components/Box';
import CardParcelModalWindow from '../modals/CardParcelModalWindow/CardParcelModalWindow';
import cn from 'classnames';
import DataTable, { DataViewHandle } from '@containers/DataTable';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { formatDate, formatSumWithCurrency } from '@utils/formatValues';
import { getCurrentLanguage } from '@utils/lang';
import { cancelUtilizationVote, getDisposal, TDisposalParcel } from '@api/apiClient/returns';
import { Button, ButtonSize } from '@components/Button';
import { toast } from 'react-toastify';
import { useMutation } from 'react-query';
import { IDisposalTabProps } from '../types';
import { Modal } from '@components/Modal';
import { Row, Column } from '@components/Grid';
import { sortByPrice, sortByStores } from '@utils/sorting';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes.ts';
import { Text, TextColor, TextSize } from '@components/Text';
import TrackingNumber from "@components/TrackingNumber";
import { useProfile } from '@contexts/ProfileContext';
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const DisposalTab: React.FC<IDisposalTabProps> = (props: IDisposalTabProps): React.ReactElement => {
  const {
    isFilterOpen,
    setIsFilterOpen,
    // onUpdate
  } = props;

  const [columnsSort, setColumnsSort] = useState<string[]>([]);
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState(false);
  const [parcel, setParcel] = useState<TDisposalParcel | undefined>(undefined);
  const [now, setNow] = useState<number>(() => Date.now());

  const dataRef = useRef<DataViewHandle>(null);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const formatCancelLeft = (voteDateIso: Date | string): string => {
    const deadline = new Date(voteDateIso).getTime() + 3 * 60 * 60 * 1000;
    const msLeft = deadline - now;
    const h = t('returnsPage.disposalTab.hoursShort');
    const m = t('returnsPage.disposalTab.minutesShort');
    if (msLeft <= 0) return `0${m}`;
    const totalMinutes = Math.ceil(msLeft / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours}${h} ${minutes}${m}` : `${minutes}${m}`;
  };

  const currentLanguage = getCurrentLanguage();
  const { account } = useProfile();
  const { t } = useTranslation();

  const variableCurrency: string = account?.region === 'TR' ? '$' : '¥';

  const cancelVoteMutation = useMutation({
    mutationFn: (id: string) => cancelUtilizationVote([id]),
    onSuccess: () => {
      toast.success(t('returnsPage.disposalTab.cancelVoteSuccess'));
      dataRef.current?.refresh?.();
    },
    onError: () => {
      toast.error(t('returnsPage.disposalTab.cancelVoteError'));
    },
  });

  // const refreshData = () => {
  //   if (dataRef.current?.refresh) {
  //     dataRef.current.refresh();
  //   }
  //   onUpdate();
  // };

  const onCloseParcel = () => {
    setIsOpenParcelWindow(false);
  };

  const handlerSortByPrice = (position: number, value: string, filters: FilterModel[]): void => {
    sortByPrice({ columnsSort, filters, position, setColumnsSort, setInnerFilters, value });
  };

  const handlerSortByStores = (position: number, value: string, filters: FilterModel[]): void => {
    sortByStores({ columnsSort, filters, position, setColumnsSort, setInnerFilters, value });
  };

  const columns: TableColumn<TDisposalParcel>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<TDisposalParcel>) => (
        <TrackingNumber
          externalId={row.data?.trackingNumber}
          trackNumber={row.data?.directStreamExternalId}
          onClick={() => {
            setIsOpenParcelWindow(true);
            setParcel(row.data);
          }}
          allowEqualNumbers
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: "200px"
    },
    {
      header: t('returnsPage.disposalTab.name'),
      accessor: (row: TableRowType<TDisposalParcel>) => (
          <a
              className={styles.link}
              href={row?.data?.url}
              rel="noreferrer"
              target="_blank"
          >
            {row?.data?.title}
          </a>
      ),
      width: '235px'
    },
    {
      header: t('returnsPage.disposalTab.priceOfProduct'),
      accessor: (row: TableRowType<TDisposalParcel>) => (
          <div className={styles.price}>
            <Text
                color={TextColor.PRIMARY}
                size={TextSize.S}
            >
              {formatSumWithCurrency(row.data.priceRub, '₽')}
            </Text>
            <Text
                color={TextColor.DARK_MUTED}
                size={TextSize.XS}
            >
              {formatSumWithCurrency(row.data.priceCny, variableCurrency)}
            </Text>
          </div>
      ),
      alignment: 'right',
      noOverflow: true,
      sortable: true,
      sortableFn: handlerSortByPrice,
      width: '100px'
    },
    {
      header: t('returnsPage.disposalTab.store'),
      accessor: (row: TableRowType<TDisposalParcel>) => (
        <AvatarWithMarketplace
          id={row.data.seller.id}
          name={row.data.seller.name}
          marketplace={row.data.marketplaceType}
        />
      ),
      alignment: 'center',
      noOverflow: true,
      sortable: true,
      sortableFn: handlerSortByStores
    },
    {
      header: t('returnsPage.disposalTab.date'),
      accessor: (row: TableRowType<TDisposalParcel>) => formatDate(row.data.utilizationVoteDate, 'dd.MM.yy'),
      alignment: 'right',
      type: TableColumnType.TERTIARY
    },
    {
      header: t("disposalTable.disposed"),
      accessor: (row: TableRowType<TDisposalParcel>) => (
        <Text
          color={TextColor.PRIMARY}
          size={TextSize.S}
        >
          {row.data.utilizationVoteInitiator === 'Seller' ? t('disposalTable.bySeller') : t('disposalTable.expired')}
        </Text>
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: t('returnsPage.disposalTab.compensation'),
      accessor: (row: TableRowType<TDisposalParcel>) => (
        <Text color={TextColor.PRIMARY} size={TextSize.S}>
          {row.data.compensationStatus === 'Received'
            ? t('returnsPage.disposalTab.compReceived')
            : row.data.compensationStatus === 'Pending'
              ? t('returnsPage.disposalTab.compPending')
              : <span className={styles.dash} aria-label="—" />}
        </Text>
      ),
      alignment: 'center',
      noOverflow: true,
    },
    {
      header: '',
      accessor: (row: TableRowType<TDisposalParcel>) =>
        row.data.canCancelVote ? (
          <Button
            size={ButtonSize.XS}
            isRounded
            title={`${t('returnsPage.disposalTab.cancelVote')} ${formatCancelLeft(row.data.utilizationVoteDate)}`}
            onClick={() => cancelVoteMutation.mutate(row.data.id)}
          />
        ) : null,
      alignment: 'right' as const,
      noOverflow: true,
      fixWidth: true,
      width: '160px',
    },
  ], [currentLanguage, columnsSort, cancelVoteMutation, now]);

  useEffect(() => {
    const columnsSort: string[] = columns.map((item: TableColumn<TDisposalParcel>) => item.sortable ? 'asc' : '');
    setColumnsSort(columnsSort);
  }, []);

  return (
      <div>
        <Box pb={5}>
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {t('returnsPage.disposalTab.info_1')}
          </Text>
        </Box>
        <Box
            pb={5}
            className={cn(!isFilterOpen && styles.hidden)}
        >
          <Column phone={12}>
            <Row
                justify="right"
                gap={5}
            >
              <Filters
                  id="returns-disposal"
                  openFilters={() => setIsFilterOpen(true)}
                  items={[
                    {
                      name: 'trackNumbers',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('filters.searchByTrackNumber')
                    },
                    {
                      name: 'sellerIds',
                      type: FilterTypes.SELLER
                    },
                    // {
                    //   name: "marketplaceType",
                    //   type: FilterTypes.MARKETPLACE,
                    // },
                    {
                      name: 'dateRange',
                      type: FilterTypes.DATE_RANGE,
                      placeholder: t('returnsPage.disposalTab.date'),
                      fieldNames: ['startDate', 'endDate']
                    },
                    {
                      name: "utilizationVoteInitiator",
                      type: FilterTypes.DISPOSED_BY,
                    },
                  ]}
                  onChange={(v, isReady) => {
                    if (isReady) {
                      setIsInitialized(true);
                    }
                    setInnerFilters(v);
                  }}
                  values={innerFilters}
                  asPanel
              />
            </Row>
          </Column>
        </Box>
        <DataTable<TDisposalParcel>
            columns={columns}
            columnsSort={columnsSort}
            dataRef={dataRef}
            defaultPageSize={10}
            filters={innerFilters}
            getData={getDisposal}
            notFound={t('returnsPage.disposalTab.noParcels')}
            pending={!isInitialized}
            queryKey="disposalParcels"
        />
        <Modal
            bodyWithoutIndentations
            isOpen={isOpenParcelWindow}
            onClickOverlay={onCloseParcel}
            onClose={onCloseParcel}
            width={900}
        >
          <CardParcelModalWindow
              callFrom="disposalTab"
              handlerSetIsOpenParcelWindow={setIsOpenParcelWindow}
              parcel={parcel!}
          />
        </Modal>
      </div>
  );
};

export default DisposalTab;
